import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Container, Reveal, SectionLabel } from './primitives';
import { TECH_GROUPS } from './constants';
import useGoogleAnalytics from '../../hooks/useGoogleAnalytics';

/**
 * Tech pill showing the real brand logo on a light tile (colored SVGs read
 * clearly against the dark theme). Falls back to monogram initials when the
 * logo asset is missing or fails to load.
 */
function TechPill({ name, logo, onClick }) {
  const [failed, setFailed] = useState(false);
  const initials = name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();

  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex items-center gap-2.5 rounded-xl border border-line bg-elevated px-3.5 py-2.5 text-left transition-all duration-200 hover:-translate-y-0.5 hover:border-accent/40"
    >
      {logo && !failed ? (
        <span
          aria-hidden
          className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-white p-1 ring-1 ring-white/10"
        >
          <img
            src={logo}
            alt=""
            loading="lazy"
            onError={() => setFailed(true)}
            className="max-h-full max-w-full object-contain"
          />
        </span>
      ) : (
        <span
          aria-hidden
          className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-accent/15 text-[10px] font-bold text-accent-400"
        >
          {initials}
        </span>
      )}
      <span className="text-sm font-medium text-white/80">{name}</span>
    </button>
  );
}

export default function TechEcosystem() {
  const { t } = useTranslation();
  const { events: gaEvents } = useGoogleAnalytics();

  return (
    <section id="technologies" className="bg-surface py-20 sm:py-28">
      <Container>
        <Reveal className="mx-auto max-w-2xl text-center">
          <SectionLabel>{t('home.ecosystem.eyebrow')}</SectionLabel>
          <h2 className="mt-5 text-3xl font-bold tracking-tight text-white sm:text-4xl">
            {t('home.ecosystem.title')}
          </h2>
          <p className="mt-4 text-lg text-white/55">{t('home.ecosystem.subtitle')}</p>
        </Reveal>

        <div className="mt-14 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {TECH_GROUPS.map((group, i) => (
            <Reveal key={group.id} delay={i * 60} className="rounded-2xl border border-line bg-white/[0.02] p-6">
              <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-white/40">
                {t(`home.ecosystem.groups.${group.id}`)}
              </h3>
              <div className="flex flex-wrap gap-2.5">
                {group.items.map((tech) => (
                  <TechPill
                    key={`${group.id}-${tech.name}`}
                    name={tech.name}
                    logo={tech.logo}
                    onClick={() => gaEvents.clickTech(tech.name, group.id)}
                  />
                ))}
              </div>
            </Reveal>
          ))}
        </div>
      </Container>
    </section>
  );
}
